import React from "react";
import Header from "@/components/Header";
import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import Footer from "@/components/Footer";
import Container from "@/components/blocks/Container";
import Link from "next/link";
import { docsColors, docsComponents } from "@/lib/docs-theme";
import DocTitle from "@/components/docs/DocTitle";
import DocSection from "@/components/docs/DocSection";
import DocCodeBlock from "@/components/docs/DocCodeBlock";
import DocAlert from "@/components/docs/DocAlert";
import DocNavFooter from "@/components/docs/DocNavFooter";

export default function ClaudeCodeSkillDocsPage() {
  return (
    <>
      <Head>
        {generateNextSeo({
          title: "Claude Code Skill - Uptime Monitor Documentation",
          description:
            "Set up uptime monitoring with natural language in Claude Code. Install the skill and manage monitors, alerts, status pages, and incidents from your AI assistant.",
          canonical: "https://uptime-monitor.io/docs/claude-code-skill",
          openGraph: {
            title: "Claude Code Skill - Uptime Monitor",
            description:
              "Set up uptime monitoring with natural language in Claude Code. Manage monitors, alerts, and incidents from your AI assistant.",
            url: "https://uptime-monitor.io/docs/claude-code-skill",
          },
        })}
      </Head>
      <div className={docsComponents.pageContainer}>
        <Header />
        <main className="flex-1">
          <Container>
            <div className="mx-auto max-w-4xl px-4 py-12">
              {/* Breadcrumb */}
              <nav className="mb-8">
                <ol className="flex items-center space-x-2 text-sm">
                  <li>
                    <Link href="/docs" className={docsColors.link.muted}>
                      Documentation
                    </Link>
                  </li>
                  <li className={docsColors.text.muted}>/</li>
                  <li className={docsColors.text.heading}>Claude Code Skill</li>
                </ol>
              </nav>

              <DocTitle
                title="Claude Code Skill"
                description="Set up and manage uptime monitoring with natural language — right from your editor."
              />

              {/* What It Does */}
              <DocSection title="What It Does">
                <p className={`${docsColors.text.body} mb-4`}>
                  The Uptime Monitor skill for{" "}
                  <a
                    href="https://code.claude.com"
                    className={docsColors.link.primary}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Claude Code
                  </a>{" "}
                  lets you manage your entire monitoring setup through natural language. Instead of
                  memorizing CLI flags, just tell Claude what you want:
                </p>
                <div className="space-y-3 mb-4">
                  {[
                    "Monitor my API at api.example.com every 10 seconds",
                    "Alert me on Slack when something goes down",
                    "What's the status of my monitors?",
                    "Create a status page for my customers",
                    "Show me recent incidents for the production API",
                    "Set up PagerDuty alerts for the on-call team",
                  ].map((prompt) => (
                    <div
                      key={prompt}
                      className={`flex items-center gap-3 ${docsColors.text.body} text-sm`}
                    >
                      <span className="text-muted-foreground font-mono">❯</span>
                      <span>{prompt}</span>
                    </div>
                  ))}
                </div>
                <p className={`${docsColors.text.body}`}>
                  The skill handles CLI installation, authentication, and translates your intent
                  into the right commands with sensible defaults.
                </p>
              </DocSection>

              {/* Installation */}
              <DocSection title="Installation">
                <p className={`${docsColors.text.body} mb-4`}>
                  Install the CLI and skill together:
                </p>
                <DocCodeBlock copyText="curl -sSfL https://cli.uptime-monitor.io/install.sh | sh -s -- --with-claude-skill">
                  <pre className="text-sm pr-10">
                    <code>{`curl -sSfL https://cli.uptime-monitor.io/install.sh | sh -s -- --with-claude-skill`}</code>
                  </pre>
                </DocCodeBlock>
                <p className={`${docsColors.text.body} mt-4 mb-4 text-sm`}>
                  Or if the CLI is already installed:
                </p>
                <DocCodeBlock copyText="uptime-monitor setup-skill">
                  <pre className="text-sm">
                    <code>{`uptime-monitor setup-skill`}</code>
                  </pre>
                </DocCodeBlock>
                <p className={`${docsColors.text.body} mt-4 text-sm`}>
                  The skill installs to <code>~/.claude/skills/uptime-monitor-cli/</code> and is
                  automatically available in Claude Code.
                </p>
              </DocSection>

              {/* How It Works */}
              <DocSection title="How It Works">
                <p className={`${docsColors.text.body} mb-4`}>
                  When you ask Claude about uptime monitoring, the skill activates and:
                </p>
                <div className="space-y-4 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-primary">1</span>
                    </div>
                    <div>
                      <p className={`font-medium ${docsColors.text.heading}`}>
                        Checks CLI is installed
                      </p>
                      <p className={`text-sm ${docsColors.text.muted}`}>
                        Offers to install it if missing
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-primary">2</span>
                    </div>
                    <div>
                      <p className={`font-medium ${docsColors.text.heading}`}>
                        Verifies authentication
                      </p>
                      <p className={`text-sm ${docsColors.text.muted}`}>
                        Guides you through <code>uptime-monitor auth</code> if needed
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-primary">3</span>
                    </div>
                    <div>
                      <p className={`font-medium ${docsColors.text.heading}`}>
                        Executes your request
                      </p>
                      <p className={`text-sm ${docsColors.text.muted}`}>
                        Runs the right CLI commands with best-practice defaults
                      </p>
                    </div>
                  </div>
                </div>

                <DocAlert variant="info">
                  <p className="text-sm">
                    The skill includes built-in knowledge of monitoring best practices — optimal
                    check intervals, region selection, fail thresholds, and alerting strategies.
                  </p>
                </DocAlert>
              </DocSection>

              {/* Alerts & Notifications */}
              <DocSection title="Alerts & Notifications">
                <p className={`${docsColors.text.body} mb-4`}>
                  Set up alerts through natural language. The skill supports all notification
                  channels:
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <th className={`text-left py-2 pr-4 ${docsColors.text.heading}`}>
                          Channel
                        </th>
                        <th className={`text-left py-2 ${docsColors.text.heading}`}>
                          Example Prompt
                        </th>
                      </tr>
                    </thead>
                    <tbody className={docsColors.text.body}>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4 font-medium">Slack</td>
                        <td className="py-2">
                          &quot;Send alerts to our #ops-alerts Slack channel&quot;
                        </td>
                      </tr>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4 font-medium">PagerDuty</td>
                        <td className="py-2">
                          &quot;Set up PagerDuty alerts for the on-call team&quot;
                        </td>
                      </tr>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4 font-medium">Email</td>
                        <td className="py-2">
                          &quot;Email ops@example.com when something goes down&quot;
                        </td>
                      </tr>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4 font-medium">Discord</td>
                        <td className="py-2">&quot;Alert our Discord #monitoring channel&quot;</td>
                      </tr>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4 font-medium">Opsgenie</td>
                        <td className="py-2">&quot;Route P1 alerts through Opsgenie&quot;</td>
                      </tr>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4 font-medium">Zendesk</td>
                        <td className="py-2">
                          &quot;Create a Zendesk ticket when a monitor goes down&quot;
                        </td>
                      </tr>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4 font-medium">incident.io</td>
                        <td className="py-2">&quot;Track incidents in incident.io&quot;</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4 font-medium">Webhook</td>
                        <td className="py-2">
                          &quot;Send a webhook to our automation endpoint&quot;
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className={`${docsColors.text.body} mt-4 text-sm`}>
                  You can also receive push notifications on your iPhone with the Uptime Monitor iOS
                  app.
                </p>
              </DocSection>

              {/* What You Can Do */}
              <DocSection title="Capabilities">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Monitors</h3>
                    <ul className={`${docsColors.text.body} text-sm space-y-1`}>
                      <li>Create HTTPS, TCP, and Ping monitors</li>
                      <li>List, update, pause, resume, delete</li>
                      <li>Check overall status</li>
                      <li>Configure SSL certificate monitoring</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Contacts</h3>
                    <ul className={`${docsColors.text.body} text-sm space-y-1`}>
                      <li>Set up any notification channel</li>
                      <li>Test contact delivery</li>
                      <li>Attach contacts to monitors</li>
                      <li>Reactivate disabled contacts</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                      Status Pages
                    </h3>
                    <ul className={`${docsColors.text.body} text-sm space-y-1`}>
                      <li>Create public status pages</li>
                      <li>Custom domains and password protection</li>
                      <li>Choose uptime period (7, 30, 90 days)</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Incidents</h3>
                    <ul className={`${docsColors.text.body} text-sm space-y-1`}>
                      <li>View recent and ongoing incidents</li>
                      <li>Get detailed error information</li>
                      <li>Add comments for post-mortems</li>
                    </ul>
                  </div>
                </div>
              </DocSection>

              {/* Learn More */}
              <DocSection title="Learn More">
                <p className={`${docsColors.text.body} mb-4`}>
                  The skill is built on the Uptime Monitor CLI. For the full command reference and
                  all available flags, see the{" "}
                  <Link href="/docs/cli" className={docsColors.link.primary}>
                    CLI documentation
                  </Link>
                  .
                </p>
                <p className={`${docsColors.text.body}`}>
                  To learn more about Claude Code skills, visit the{" "}
                  <a
                    href="https://code.claude.com/docs/en/skills"
                    className={docsColors.link.primary}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Claude Code documentation
                  </a>
                  .
                </p>
              </DocSection>

              <DocNavFooter
                prev={{
                  href: "/docs/cli",
                  text: "CLI Tool",
                }}
                next={{
                  href: "/docs/best-practices",
                  text: "Best Practices",
                }}
              />
            </div>
          </Container>
        </main>
        <Footer />
      </div>
    </>
  );
}
